import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import PhoneIcon from '@material-ui/icons/Phone';
import SmartphoneIcon from '@material-ui/icons/Smartphone';
import PlaceIcon from '@material-ui/icons/Place';
import { RootState } from '../../../../../redux/root';
import { setSelectedInspection, setShowPopup } from '../../../../../redux/slices/inspectionsSlice';

const ContactsPopup = () => {
  const { selectedInspection, permitUsers } = useSelector((s: RootState) => s.inspections);
  const showPopup = useSelector((s: RootState) => s.inspections.showPopup);
  const dispatch = useDispatch();

  if (!showPopup) return null;

  const selectUser = user => {
    if (!user.mobilePhoneNumber) return;
    dispatch(
      setSelectedInspection({
        ...selectedInspection,
        contactFirstName: user.firstName,
        contactLastName: user.lastName,
        contact: user.id,
        phoneNumber: user.phoneNumber,
        cellPhone: user.mobilePhoneNumber,
        contactId: user.id,
      }),
    );
    dispatch(setShowPopup('none'));
  };

  return (
    <Backdrop>
      <Wrapper>
        <Caption>Contacts</Caption>
        <List>
          {permitUsers.length === 0 && <Empty>No contacts available</Empty>}
          {permitUsers.map((user, index) => {
            const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
            const isSelectable = Boolean(user.mobilePhoneNumber);
            return (
              <Card
                key={user.id ?? index}
                type="button"
                $selectable={isSelectable}
                disabled={!isSelectable}
                onClick={() => selectUser(user)}
                title={isSelectable ? 'Select contact' : 'Cellphone required to select this contact'}
              >
                <NameRow>
                  <Name>{name || '—'}</Name>
                  {!isSelectable && <Pill>No cellphone</Pill>}
                </NameRow>
                {user.address && (
                  <MetaRow>
                    <PlaceIcon />
                    <span>{user.address}</span>
                  </MetaRow>
                )}
                <MetaItem>
                  <Eyebrow>
                    <PhoneIcon />
                    Phone Number
                  </Eyebrow>
                  <MetaValue>{user.phoneNumber || '—'}</MetaValue>
                </MetaItem>
                <MetaItem>
                  <Eyebrow $danger={!user.mobilePhoneNumber}>
                    <SmartphoneIcon />
                    Cellphone
                  </Eyebrow>
                  <MetaValue $danger={!user.mobilePhoneNumber}>{user.mobilePhoneNumber || '—'}</MetaValue>
                </MetaItem>
              </Card>
            );
          })}
        </List>

        <Footer>
          <CloseButton type="button" onClick={() => dispatch(setShowPopup('none'))}>
            Close
          </CloseButton>
        </Footer>
      </Wrapper>
    </Backdrop>
  );
};

export default ContactsPopup;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(15, 23, 42, 0.45);
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 440px;
  max-height: calc(100% - 32px);
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.18);
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-family: ${FONT_STACK};
`;

const Caption = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
  text-align: center;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 360px;
  overflow-y: auto;
  padding-right: 4px;
`;

const Empty = styled.div`
  padding: 24px 12px;
  text-align: center;
  color: var(--neutral-500);
  font-size: 0.875rem;
`;

const Card = styled.button<{ $selectable: boolean }>`
  width: 100%;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  font-family: inherit;
  cursor: ${({ $selectable }) => ($selectable ? 'pointer' : 'not-allowed')};
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

  ${({ $selectable }) =>
    $selectable &&
    css`
      &:hover {
        background: var(--primary-50);
        border-color: var(--primary-200, #b6d8ec);
      }

      &:focus-visible {
        outline: none;
        box-shadow: var(--shadow-focus);
        border-color: var(--primary-600);
      }
    `}

  &:disabled {
    opacity: 0.7;
  }
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Name = styled.span`
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--mainText);
`;

const Pill = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--danger-50, #fdecec);
  color: var(--danger-700, #9a2523);
  border: 1px solid var(--danger-200, #f1b3b3);
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  color: var(--neutral-700);

  svg {
    font-size: 16px;
    color: var(--neutral-500);
    flex-shrink: 0;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Eyebrow = styled.span<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ $danger }) => ($danger ? 'var(--danger-600, #b91c1c)' : 'var(--neutral-500)')};

  svg {
    font-size: 13px;
  }
`;

const MetaValue = styled.span<{ $danger?: boolean }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ $danger }) => ($danger ? 'var(--danger-600, #b91c1c)' : 'var(--mainText)')};
  font-variant-numeric: tabular-nums;
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
`;

const CloseButton = styled.button`
  min-width: 140px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-family: ${FONT_STACK};
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;
